/**
 * The map's live presentation axes, as context.
 *
 * Two questions are open at once and they are tuned against each other, which
 * is why they travel together: how a pin reads AT REST (it is one of many, so
 * it must read as texture) and how much of the basemap's own places show
 * behind it. Turn ambient POIs up and a loud idle pin becomes noise; turn them
 * off and a quiet pin has nothing to compete with. One provider, one value.
 *
 * A pin never prints a name in either state — the name lives in the rail card
 * the pick selects, the click popup and the aria-label.
 *
 * Everything else about a map is retired and baked: the three cartography inks
 * (Sovereign / Whisper / Petrol) and the chosen-pin Halo all locked, so they
 * live in `map-cartography.ts` and `MapExpandedSurface.css` as ordinary code.
 *
 * The default is the point of the file as much as the provider is. Every
 * existing embedder mounts NO provider — the transcript block, the rail cards,
 * the compact preview's expansion — and each of them keeps rendering the baked
 * picks with no edit at the call site. `useContext` returning
 * `MAP_PRESENTATION_DEFAULT` is what guarantees that: the provider is an
 * override for the one surface that has a control, not a requirement.
 */

'use client';

import {
  createContext,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  MAP_AMBIENT_POI_DEFAULT,
  MapAmbientPoiVariant,
} from './map-ambient-poi';
import { MAP_POI_FOLLOW_UP_DEFAULT, MapPoiFollowUp } from './map-poi-follow-up';

/** The at-rest treatment of every pin that is not chosen. */
export enum MapPinIdleVariant {
  /** The shipped pick: the teardrop silhouette in dark glass — a muted twin
   *  of the chosen pin — with the place-kind glyph as its only content. */
  Glyph = 'Glyph',
  /** A round neutral chip: no teardrop, no accent at rest, the glyph on
   *  frosted dark glass. The teardrop then belongs to the chosen pin alone. */
  Frost = 'Frost',
  /** The teardrop with a low-key accent tint: mint-lined, mint glyph, still
   *  glowless — the family read halfway between Glyph and the chosen pin. */
  Ember = 'Ember',
}

/** Option list the control renders, shipped pick first. */
export const MAP_PIN_IDLE_VARIANTS: readonly MapPinIdleVariant[] = [
  MapPinIdleVariant.Glyph,
  MapPinIdleVariant.Frost,
  MapPinIdleVariant.Ember,
];

/** Every axis as one value, so the consumers have a single context to read and
 *  a provider-less embedder resolves all of them from one constant. */
export interface MapPresentation {
  readonly pinIdle: MapPinIdleVariant;
  readonly ambientPoi: MapAmbientPoiVariant;
  /** Which of the three ambient-POI overlaps is being resolved on the canvas,
   *  or null for none of them. Unlike the two above it does not change how a
   *  place is DRAWN — it changes what the map does about a place colliding with
   *  something else, so "no opinion" is a state the other two do not have. */
  readonly poiFollowUp: MapPoiFollowUp | null;
}

/** What a map looks like with no control attached — the shipped picks. */
export const MAP_PRESENTATION_DEFAULT: MapPresentation = {
  pinIdle: MapPinIdleVariant.Glyph,
  ambientPoi: MAP_AMBIENT_POI_DEFAULT,
  poiFollowUp: MAP_POI_FOLLOW_UP_DEFAULT,
};

const MapPresentationContext = createContext<MapPresentation>(
  MAP_PRESENTATION_DEFAULT,
);

interface CmarkdownmapPresentationProviderProps {
  pinIdle: MapPinIdleVariant;
  ambientPoi: MapAmbientPoiVariant;
  /** A provider always names one; it is the ABSENCE of a provider that means
   *  none, and that resolves through `MAP_PRESENTATION_DEFAULT` instead. */
  poiFollowUp: MapPoiFollowUp;
  children: ReactNode;
}

/**
 * Publishes one presentation to every map below it.
 *
 * The value is re-derived from the axes rather than passed straight through,
 * because a consumer applies two of them from effects keyed on them: a control
 * that rebuilds the object inline on every render — which is what a tweaks
 * panel holding three pieces of state does — would hand a fresh identity down
 * on every unrelated keystroke and re-add the POI layer each time. Memoising on
 * the primitives collapses that to exactly the renders where an axis moved.
 */
export function CmarkdownmapPresentationProvider({
  pinIdle,
  ambientPoi,
  poiFollowUp,
  children,
}: CmarkdownmapPresentationProviderProps): ReactElement {
  const value = useMemo<MapPresentation>(
    () => ({ pinIdle, ambientPoi, poiFollowUp }),
    [pinIdle, ambientPoi, poiFollowUp],
  );

  return (
    <MapPresentationContext.Provider value={value}>
      {children}
    </MapPresentationContext.Provider>
  );
}

export function useMapPinIdleVariant(): MapPinIdleVariant {
  return useContext(MapPresentationContext).pinIdle;
}

export function useMapAmbientPoiVariant(): MapAmbientPoiVariant {
  return useContext(MapPresentationContext).ambientPoi;
}

export function useMapPoiFollowUp(): MapPoiFollowUp | null {
  return useContext(MapPresentationContext).poiFollowUp;
}
